import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import useUploadAsset from 'packages/kakapo-ui-application/hooks/useUploadAsset';

const Row = styled(Flex)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
	align-items: center;
`;

const Icon = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
	width: 34px;
	height: 34px;
	border-radius: 4px;
	background: ${(props) => props.theme.colors.slateBlue};
	color: white;
	text-align: center;
	font-size: 13px;
	line-height: 34px;
`;

const Link = styled.a`
	color: ${(props) => props.theme.colors.slateBlue};
	font-size: 16px;
	text-decoration: underline;
	flex-grow: 0;
	flex-shrink: 1;
	word-break: break-all;
`;

const CreatedBy = styled(Box)`
	flex-grow: 0;
	flex-shrink: 0;
	color: ${(props) => props.theme.colors.lightBlueDark};
`;

function UserDocument(props: { user: graphql.User; document: graphql.UserDocument }) {
	const ext = props.document.name.split('.')[1];

	const [commit, { loading }] = graphql.useDeleteUserDocumentMutation({
		variables: {
			input: {
				userID: props.user.id,
				assetID: props.document.asset.id,
			},
		},
	});

	return (
		<Row py={12} px={12}>
			<Icon mr={3}>{ext ? `.${ext}` : '?'}</Icon>
			<Link href={props.document.asset.source.url} download target="_blank">
				{props.document.name}
			</Link>
			<Box flexGrow={1} />
			<CreatedBy ml={3}>
				{props.document.createdBy.firstName} {props.document.createdBy.lastName}
			</CreatedBy>
			{props.document.viewerCanDelete && (
				<Flex ml={3} width={28} justifyContent="center" flexShrink={0} flexGrow={0}>
					{loading ? (
						<Spinner isLoading size="18px" verticalMargin={0} />
					) : (
						<DeleteButton onClick={() => commit()} />
					)}
				</Flex>
			)}
		</Row>
	);
}

function UserDocumentUpload(props: { user: graphql.User }) {
	const { t } = useTranslation();
	const ref = useRef<HTMLInputElement>(null);

	const [create] = graphql.useCreateUserDocumentMutation();

	const [upload, { loading }] = useUploadAsset<File>(async (assetID, file) => {
		await create({
			variables: {
				input: {
					userID: props.user.id,
					assetID,
					name: file.name,
				},
			},
		});
	});

	async function onChange() {
		if (!ref.current) {
			return;
		}

		const file = ref.current.files?.[0];

		if (!file) {
			return;
		}

		await upload(file);
	}

	return (
		<>
			{loading ? (
				<Spinner isLoading size="18px" verticalMargin={0} />
			) : (
				<>
					<input ref={ref} type="file" style={{ display: 'none' }} onChange={onChange} />
					<Button size={ButtonSize.Small} onClick={() => ref.current?.click()}>
						{t('user.documents.uploadButton.title')}
					</Button>
				</>
			)}
		</>
	);
}

export default function UserDocuments(props: { user: graphql.User }) {
	const { t } = useTranslation();

	const query = graphql.useUserDocumentsQuery({ variables: { id: props.user.id } });

	return (
		<>
			<Flex alignItems="center">
				<SectionTitle>{t('user.documents.title')}</SectionTitle>
				{props.user.viewerCanCreateDocuments && (
					<Box flexGrow={0} flexShrink={0} ml={2}>
						<UserDocumentUpload user={props.user} />
					</Box>
				)}
			</Flex>
			<Column width={1 / 2}>
				{query.data && query.data.user.documents.length > 0 ? (
					query.data.user.documents.map((doc) => (
						<UserDocument key={doc.id} user={props.user} document={doc as graphql.UserDocument} />
					))
				) : query.loading ? (
					<Spinner isLoading size="18px" />
				) : (
					<Box textAlign="center" mt={3}>
						<Paragraph color="slateBlue">{t('user.documents.empty')}</Paragraph>
					</Box>
				)}
			</Column>
		</>
	);
}
